/**
 * Módulo de funções de agregação para análise de dados de atendimentos
 * 
 * Este arquivo contém funções utilitárias para processar e agregar dados de atendimentos
 * para preparação de visualizações gráficas e análises estatísticas.
 */

// Importação de funções utilitárias de manipulação de datas
import {
  formatDate,         // Formata uma data para exibição
  convertToInputDate, // Converte uma data para o formato utilizado em inputs HTML
  getDiasFromFiltro   // Converte um texto de filtro em um número de dias para análise
} from './date';

/**
 * Agrega dados totais de atendimentos por status
 * 
 * Esta função processa um array de atendimentos e retorna um objeto com a contagem
 * de atendimentos por status (Total, Em Aberto, Concluídos) para uso em gráficos.
 * 
 * @param {Array} atendimentos - Array de objetos representando atendimentos
 * @param {boolean} atendimentos[].concluido - Indica se o atendimento está concluído
 * @returns {Object} Objeto contendo labels, dados e contagens para visualização gráfica
 * @returns {Array<string>} Object.labels - Rótulos para o gráfico ["Total", "Em Aberto", "Concluídos"]
 * @returns {Array<number>} Object.data - Valores correspondentes aos rótulos
 * @returns {number} Object.total - Número total de atendimentos
 * @returns {number} Object.emAberto - Número de atendimentos em aberto
 * @returns {number} Object.concluidos - Número de atendimentos concluídos
 */
export function aggregateAtendimentosPorStatus(atendimentos) {
  // Validação de entrada
  if (!Array.isArray(atendimentos)) {
    return {
      labels: ['Total', 'Em Aberto', 'Concluídos'],
      data: [0, 0, 0],
      total: 0,
      emAberto: 0,
      concluidos: 0
    };
  }

  // Calcula o número total de atendimentos
  const total = atendimentos.length;

  // Filtra e conta os atendimentos concluídos
  const concluidos = atendimentos.filter(a => a && a.concluido).length;

  // Calcula a quantidade de atendimentos em aberto (total - concluídos)
  const emAberto = total - concluidos;

  // Retorna objeto com dados formatados para visualização
  return {
    labels: ['Total', 'Em Aberto', 'Concluídos'], // Rótulos para o gráfico
    data: [total, emAberto, concluidos],          // Valores para cada rótulo
    total,                                       // Total de atendimentos (para acesso direto)
    emAberto,                                    // Quantidade em aberto (para acesso direto)
    concluidos                                   // Quantidade concluídos (para acesso direto)
  };
}

/**
 * Agrega mensagens por período baseado no filtro selecionado (NOVA FUNÇÃO PRINCIPAL)
 * 
 * Esta função adapta-se automaticamente ao filtro e decide se deve agrupar por dia, mês ou ano.
 * É a função principal para o novo dashboard com gráfico único.
 * 
 * @param {Array} atendimentos - Array de objetos representando atendimentos
 * @param {string} filtro - Filtro de período selecionado
 * @param {Object} options - Opções adicionais para agregação
 * @returns {Object} Objeto com labels e data para o gráfico
 */
export function aggregateAtendimentosPorPeriodo(atendimentos, filtro) {
  if (!atendimentos || atendimentos.length === 0) {
    return { labels: [], data: [] };
  }

  // Determina o tipo de agregação baseado no filtro
  const shouldGroupByMonth = [
    'Últimos 3 meses',
    'Últimos 6 meses',
    'Últimos 12 meses'
  ].includes(filtro);

  const shouldGroupByYear = filtro === 'Todos' && hasMultipleYears(atendimentos);

  if (shouldGroupByYear) {
    return aggregateByYear(atendimentos);
  } else if (shouldGroupByMonth) {
    return aggregateByMonth(atendimentos, filtro);
  } else {
    return aggregateByDay(atendimentos, filtro);
  }
}


/**
 * Verifica se os atendimentos abrangem múltiplos anos
 * @param {Array} atendimentos - Array de atendimentos
 * @returns {boolean} True se há dados de múltiplos anos
 */
function hasMultipleYears(atendimentos) {
  const years = new Set();
  atendimentos.forEach(atendimento => {
    if (atendimento && atendimento.dataHoraCadastroAtualizado) {
      try {
        const date = new Date(atendimento.dataHoraCadastroAtualizado);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          years.add(year);
        }
      } catch (error) {
        console.warn('Data inválida encontrada:', atendimento.dataHoraCadastroAtualizado);
      }
    }
  });
  return years.size > 1;
}

/**
 * Agrega atendimentos por ano
 * @param {Array} atendimentos - Array de atendimentos
 * @returns {Object} Objeto com labels e data agrupados por ano
 */
function aggregateByYear(atendimentos) {
  const yearMap = {};

  atendimentos.forEach(atendimento => {
    if (atendimento && atendimento.dataHoraCadastroAtualizado) {
      try {
        const date = new Date(atendimento.dataHoraCadastroAtualizado);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          yearMap[year] = (yearMap[year] || 0) + 1;
        }
      } catch (error) {
        console.warn('Erro ao processar data:', atendimento.dataHoraCadastroAtualizado);
      }
    }
  });

  const sortedYears = Object.keys(yearMap).sort((a, b) => Number(a) - Number(b));
  const labels = sortedYears;
  const data = sortedYears.map(year => yearMap[year]);

  return { labels, data };
}

/**
 * Agrega atendimentos por mês
 * @param {Array} atendimentos - Array de atendimentos
 * @param {string} filtro - Filtro de período
 * @returns {Object} Objeto com labels e data agrupados por mês
 */
function aggregateByMonth(atendimentos, filtro) {
  const monthMap = {};
  const now = new Date();

  // Define quantos meses incluir baseado no filtro
  const monthsToInclude = getMonthsCount(filtro);

  atendimentos.forEach(atendimento => {
    if (atendimento && atendimento.dataHoraCadastroAtualizado) {
      try {
        const date = new Date(atendimento.dataHoraCadastroAtualizado);
        if (!isNaN(date.getTime())) {
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          monthMap[monthKey] = (monthMap[monthKey] || 0) + 1;
        }
      } catch (error) {
        console.warn('Erro ao processar data:', atendimento.dataHoraCadastroAtualizado);
      }
    }
  });

  // Gera lista de meses para o período
  const labels = [];
  const data = [];

  for (let i = monthsToInclude - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });

    labels.push(monthLabel);
    data.push(monthMap[monthKey] || 0);
  }

  return { labels, data };
}

/**
 * Agrega atendimentos por dia (função interna otimizada)
 * @param {Array} atendimentos - Array de atendimentos
 * @param {string} filtro - Filtro de período
 * @returns {Object} Objeto com labels e data agrupados por dia
 */
function aggregateByDay(atendimentos, filtro) {
  const dayMap = {};

  // Conta atendimentos por dia
  atendimentos.forEach(a => {
    if (a && a.dataHoraCadastroAtualizado) {
      try {
        const inputDate = convertToInputDate(a.dataHoraCadastroAtualizado);
        dayMap[inputDate] = (dayMap[inputDate] || 0) + 1;
      } catch (error) {
        console.warn('Erro ao processar data:', a.dataHoraCadastroAtualizado);
      }
    }
  });

  // Tratamento para o filtro "Todos" - exibe todos os dias no intervalo de dados
  if (filtro === "Todos") {
    const keys = Object.keys(dayMap).sort();
    if (keys.length === 0) return { labels: [], data: [] };

    try {
      const start = new Date(keys[0]);
      const end = new Date(keys[keys.length - 1]);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return { labels: [], data: [] };
      }

      const labels = [];
      const data = [];

      const current = new Date(start);
      while (current <= end) {
        const iso = current.toISOString();
        const inputDate = convertToInputDate(iso);

        labels.push(formatDate(iso));
        data.push(dayMap[inputDate] || 0);

        current.setDate(current.getDate() + 1);
      }

      return { labels, data };
    } catch (error) {
      console.error('Erro ao processar intervalo de datas:', error);
      return { labels: [], data: [] };
    }
  }
  // Tratamento para filtros específicos de período
  else {
    try {
      const dias = getDiasFromFiltro(filtro);
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - dias + 1);

      const labels = [];
      const data = [];

      const current = new Date(startDate);
      while (current <= today) {
        const iso = current.toISOString();
        const inputDate = convertToInputDate(iso);

        labels.push(formatDate(iso));
        data.push(dayMap[inputDate] || 0);

        current.setDate(current.getDate() + 1);
      }

      return { labels, data };
    } catch (error) {
      console.error('Erro ao processar filtro de período:', error);
      return { labels: [], data: [] };
    }
  }
}

/**
 * Retorna o número de meses baseado no filtro
 * @param {string} filtro - Filtro de período
 * @returns {number} Número de meses
 */
function getMonthsCount(filtro) {
  switch (filtro) {
    case 'Últimos 3 meses': return 3;
    case 'Últimos 6 meses': return 6;
    case 'Últimos 12 meses': return 12;
    default: return 3;
  }
}

/**
 * Agrega atendimentos por dia para análise de evolução temporal (FUNÇÃO LEGADA - MANTIDA PARA COMPATIBILIDADE)
 * 
 * Esta função processa um array de atendimentos e os agrupa por dia, gerando dados
 * para visualização da evolução temporal dos atendimentos. O comportamento varia
 * conforme o filtro de período selecionado.
 * 
 * @param {Array} atendimentos - Array de objetos representando atendimentos
 * @param {Object} atendimentos[] - Objeto representando um atendimento
 * @param {string|Date} atendimentos[].dataHoraCadastroAtualizado - Data de cadastro/atualização do atendimento
 * @param {string} filtro - Texto do filtro de período (ex: "Todos", "Últimos 7 dias", etc.)
 * @returns {Object} Objeto contendo labels e dados para visualização gráfica temporal
 * @returns {Array<string>} Object.labels - Array de datas formatadas para exibição
 * @returns {Array<number>} Object.data - Quantidade de atendimentos para cada data
 */
export function aggregateAtendimentosPorDia(atendimentos, filtro) {
  // Mantém compatibilidade com código existente redirecionando para a nova função
  return aggregateAtendimentosPorPeriodo(atendimentos, filtro);
}

export const aggregateMensagensPorDiaExato = (atendimentos, dateFilter) => {
  if (!Array.isArray(atendimentos) || atendimentos.length === 0) {
    return { labels: [], data: [] };
  }

  const dayMap = {};

  // Conta mensagens por dia
  atendimentos.forEach(a => {
    if (a && a.dataHoraCadastroAtualizado) {
      try {
        const inputDate = convertToInputDate(a.dataHoraCadastroAtualizado);
        dayMap[inputDate] = (dayMap[inputDate] || 0) + 1;
      } catch (error) {
        console.warn('Erro ao processar data_criacao:', a.data_criacao);
      }
    }
  });

  // Caso "Todos" - gera intervalo completo do range de dados
  if (dateFilter === 'Todos') {
    const keys = Object.keys(dayMap).sort();
    if (keys.length === 0) return { labels: [], data: [] };

    const start = new Date(keys[0]);
    const end = new Date(keys[keys.length - 1]);

    const labels = [];
    const data = [];

    const current = new Date(start);
    while (current <= end) {
      const iso = current.toISOString();
      const inputDate = convertToInputDate(iso);

      labels.push(formatDate(iso));
      data.push(dayMap[inputDate] || 0);

      current.setDate(current.getDate() + 1);
    }

    return { labels, data };
  }
  // Filtros "Últimos X dias" e "Mês atual"
  else {
    try {
      const dias = getDiasFromFiltro(dateFilter);
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - dias + 1);

      const labels = [];
      const data = [];

      const current = new Date(startDate);
      while (current <= today) {
        const iso = current.toISOString();
        const inputDate = convertToInputDate(iso);

        labels.push(formatDate(iso));
        data.push(dayMap[inputDate] || 0);

        current.setDate(current.getDate() + 1);
      }

      return { labels, data };
    } catch (error) {
      console.error('Erro ao processar filtro de período:', error);
      return { labels: [], data: [] };
    }
  }
};
